import { useTable, useSelect } from "@refinedev/antd";
import { Card, Table, Space, Input, Select, Button, Tag, Tooltip } from "antd";
import type { TableProps } from "antd";
import {
  ImportOutlined,
  PlusOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  EditOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { PageHeader } from "../../components/common/PageHeader";
import { useTranslation } from "react-i18next";
import type { Producer } from "../../types";
import { useEffect, useState } from "react";
import { usePageTitle } from "../../hooks";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useGetIdentity } from "@refinedev/core";
import type { UserInfo } from "../../types/auth";
import { canWriteData, isEurosemillasScoped } from "../../types/auth";

/** ID of the "España" portfolio — used as default filter */
const SPAIN_PORTFOLIO_ID = 1;

type CertStatus = "synced" | "pending" | "missing";

const certStatusOf = (record: Producer): CertStatus => {
  if (!record.certification_synced_at) return "missing";
  if (record.modified && record.modified > record.certification_synced_at) return "pending";
  return "synced";
};

/**
 * ProducerList Component
 *
 * Every piece of table state (page, sort, search, portfolio + cert_status
 * filters) lives in the URL search params. When the admin clicks into a
 * producer and pulses Back, the browser restores the URL and we rebuild
 * the table to match — same page, same filters, same sort.
 */
export const ProducerList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: identity } = useGetIdentity<UserInfo>();
  const canWrite = canWriteData(identity);

  // Read URL → seed the initial state of every control + useTable.
  const initialPage = Math.max(1, Number(searchParams.get("page")) || 1);
  const initialSortField = searchParams.get("sortBy") || "name";
  const initialSortOrder: "asc" | "desc" =
    searchParams.get("order") === "desc" ? "desc" : "asc";

  const [portfolioId, setPortfolioId] = useState<number | undefined>(
    searchParams.get("portfolio")
      ? Number(searchParams.get("portfolio"))
      : SPAIN_PORTFOLIO_ID,
  );
  // ESA users no tienen España (1) en su scope. Al cargar `identity`,
  // si el URL no fuerza otro portfolio, retargeteamos al portfolio
  // asignado al ESA para que el Select muestre una opción válida en
  // lugar de un valor colgado sin label.
  useEffect(() => {
    if (searchParams.get("portfolio")) return;
    if (!identity) return;
    if (!isEurosemillasScoped(identity)) return;
    const p = identity.app_user?.portfolio;
    if (p && p !== portfolioId) setPortfolioId(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity]);
  const [searchText, setSearchText] = useState<string>(
    searchParams.get("q") || "",
  );
  const [certStatusFilter, setCertStatusFilter] = useState<CertStatus | undefined>(
    (searchParams.get("cert_status") as CertStatus) || undefined,
  );

  usePageTitle(t("producers.title", "Gestión de Productores"));

  // Permanent filters are recomputed each render from local state. When the
  // user changes a filter, the local state updates → useTable picks up the
  // new filters and refetches automatically.
  const permanentFilters: any[] = [];
  if (portfolioId) permanentFilters.push({ field: "portfolio", operator: "eq", value: portfolioId });
  if (certStatusFilter) permanentFilters.push({ field: "cert_status", operator: "eq", value: certStatusFilter });
  if (searchText.trim()) permanentFilters.push({ field: "q", operator: "eq", value: searchText.trim() });

  const { tableProps } = useTable<Producer>({
    resource: "producers",
    pagination: {
      mode: "server",
      pageSize: 25,
      currentPage: initialPage,
    },
    filters: {
      permanent: permanentFilters,
    },
    sorters: {
      mode: "server",
      initial: [{ field: initialSortField, order: initialSortOrder }],
    },
    syncWithLocation: false,
  });

  const { selectProps: portfolioSelectProps } = useSelect({
    resource: "portfolios",
    optionLabel: "name" as any,
    optionValue: "id" as any,
  });

  // ---- URL helpers ---------------------------------------------------------

  /** Merge keys into the current search params, then update the URL. */
  const updateParams = (changes: Record<string, string | null | undefined>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([k, v]) => {
      if (v == null || v === "") params.delete(k);
      else params.set(k, v);
    });
    setSearchParams(params, { replace: true });
  };

  // Wrap antd Table's onChange so pagination + sort changes also persist in
  // the URL while still letting refine update its internal table state.
  // tableProps.onChange's generic is wider than Producer here; cast at the
  // boundary instead of fighting refine's types.
  const tableOnChange = tableProps.onChange as TableProps<Producer>["onChange"];
  const onTableChange: TableProps<Producer>["onChange"] = (
    pagination,
    filters,
    sorter,
    extra,
  ) => {
    const page = pagination?.current ?? 1;
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortField = s?.columnKey ? String(s.columnKey)
      : s?.field ? String(s.field)
      : null;
    const sortOrder = s?.order === "ascend"
      ? "asc"
      : s?.order === "descend"
        ? "desc"
        : null;

    updateParams({
      page: page > 1 ? String(page) : null,
      sortBy: sortOrder ? sortField : null,
      order: sortOrder,
    });

    tableOnChange?.(pagination, filters, sorter, extra);
  };

  // ---- Handlers ------------------------------------------------------------

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchText(trimmed);
    // A new search invalidates the current page.
    updateParams({ q: trimmed || null, page: null });
  };

  const handlePortfolioChange = (value: number | undefined) => {
    setPortfolioId(value);
    updateParams({
      portfolio: value ? String(value) : null,
      page: null,
    });
  };

  const handleCertStatusChange = (value: CertStatus | undefined) => {
    setCertStatusFilter(value);
    updateParams({
      cert_status: value || null,
      page: null,
    });
  };

  return (
    <Card>
      <PageHeader
        icon={<TeamOutlined />}
        accent="#52C41A"
        title={t("producers.title", "Gestión de Productores")}
        subtitle={t(
          "producers.subtitle",
          "Listado de productores y sus carteras",
        )}
        extra={
          <Space>
            <Button
              icon={<ImportOutlined />}
              onClick={() => navigate("/producers/contacts/import")}
            >
              {t("contact_import.menu_label", "Importar contactos")}
            </Button>
            {canWrite && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/producers/create")}
              >
                {t("producers.actions.create", "Crear Productor")}
              </Button>
            )}
          </Space>
        }
      />
      <Space size="middle" style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder={t("producers.search_placeholder", "Buscar por nombre, responsable o CIF...")}
          allowClear
          enterButton={<SearchOutlined />}
          size="middle"
          style={{ width: 400 }}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (!e.target.value.trim()) handleSearch("");
          }}
          onSearch={handleSearch}
        />
        <Select<number>
          options={portfolioSelectProps.options}
          loading={portfolioSelectProps.loading}
          placeholder={t("producers.fields.portfolio", "Cartera")}
          allowClear
          style={{ width: 220 }}
          value={portfolioId}
          onChange={handlePortfolioChange}
        />
        <Select<CertStatus>
          placeholder={t("producers.fields.cert_status", "Certification")}
          allowClear
          style={{ width: 220 }}
          value={certStatusFilter}
          onChange={handleCertStatusChange}
          options={[
            { value: "synced", label: t("producers.cert_status.synced", "Sincronizado") },
            { value: "pending", label: t("producers.cert_status.pending", "Cambios pendientes") },
            { value: "missing", label: t("producers.cert_status.missing", "No existe") },
          ]}
        />
      </Space>

      <Table
        {...tableProps}
        onChange={onTableChange}
        rowKey="id"
        scroll={{ x: 1200 }}
        // El click en cualquier parte de la fila lleva al detalle. Antes
        // había un botón "Ver" dedicado — demasiada superficie para una
        // acción tan común. `fromList` propaga filtros + página + sort
        // para que "Volver" restaure la vista exacta.
        onRow={(record: Producer) => ({
          onClick: () =>
            navigate(`/producers/show/${record.id}`, {
              state: { fromList: location.search },
            }),
          style: { cursor: "pointer" },
        })}
      >
        <Table.Column
          dataIndex="name"
          title={t("producers.fields.name", "Nombre")}
          sorter
          width={180}
          defaultSortOrder={initialSortField === "name" ? (initialSortOrder === "desc" ? "descend" : "ascend") : null}
        />
        <Table.Column
          dataIndex="cif"
          title={t("producers.fields.cif", "CIF / RUC")}
          width={130}
          sorter
          defaultSortOrder={initialSortField === "cif" ? (initialSortOrder === "desc" ? "descend" : "ascend") : null}
          render={(value: string | null) => value || "-"}
        />
        <Table.Column
          dataIndex="portfolio_name"
          key="portfolio__name"
          title={t("producers.fields.portfolio", "Cartera")}
          width={150}
          sorter
          defaultSortOrder={initialSortField === "portfolio__name" ? (initialSortOrder === "desc" ? "descend" : "ascend") : null}
          render={(value: string | null) => value || "-"}
        />
        <Table.Column
          dataIndex="agent_name"
          key="agent__name"
          title={t("producers.fields.agent", "Responsable")}
          width={150}
          sorter
          defaultSortOrder={initialSortField === "agent__name" ? (initialSortOrder === "desc" ? "descend" : "ascend") : null}
          render={(value: string | null) => value || "-"}
        />
        <Table.Column
          dataIndex="farms_count"
          title={t("producers.fields.farms_count", "Nº Fincas")}
          width={110}
          align="center"
          sorter
          defaultSortOrder={initialSortField === "farms_count" ? (initialSortOrder === "desc" ? "descend" : "ascend") : null}
        />
        <Table.Column
          dataIndex="licensed_surface"
          title={t("producers.fields.licensed_surface", "Sup. Licenciada")}
          width={140}
          align="right"
          sorter
          defaultSortOrder={initialSortField === "licensed_surface" ? (initialSortOrder === "desc" ? "descend" : "ascend") : null}
          render={(value: number) => value != null ? value.toFixed(2) : "0.00"}
        />
        <Table.Column
          dataIndex="audited_surface"
          title={t("producers.fields.audited_surface", "Sup. Auditada")}
          width={140}
          align="right"
          sorter
          defaultSortOrder={initialSortField === "audited_surface" ? (initialSortOrder === "desc" ? "descend" : "ascend") : null}
          render={(value: number) => value != null ? value.toFixed(2) : "0.00"}
        />
        <Table.Column<Producer>
          title={t("producers.fields.cert_status", "Certification")}
          dataIndex="certification_synced_at"
          key="certification_synced_at"
          width={150}
          align="center"
          sorter
          defaultSortOrder={initialSortField === "certification_synced_at" ? (initialSortOrder === "desc" ? "descend" : "ascend") : null}
          render={(_: unknown, record: Producer) => {
            const s = certStatusOf(record);
            if (s === "synced") {
              return (
                <Tooltip title={t("producers.cert_status.synced_help", "Está sincronizado en Certification")}>
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    {t("producers.cert_status.synced", "Sincronizado")}
                  </Tag>
                </Tooltip>
              );
            }
            if (s === "pending") {
              return (
                <Tooltip title={t("producers.cert_status.pending_help", "Tiene cambios locales sin enviar a Certification")}>
                  <Tag color="gold" icon={<ExclamationCircleOutlined />}>
                    {t("producers.cert_status.pending", "Cambios pendientes")}
                  </Tag>
                </Tooltip>
              );
            }
            return (
              <Tooltip title={t("producers.cert_status.missing_help", "No existe en Certification todavía")}>
                <Tag color="default" icon={<CloseCircleOutlined />}>
                  {t("producers.cert_status.missing", "No existe")}
                </Tag>
              </Tooltip>
            );
          }}
        />
        <Table.Column
          title={t("common.actions", "Acciones")}
          dataIndex="actions"
          width={120}
          fixed="right"
          render={(_: unknown, record: Producer) => (
            // Propagamos `fromList` con los search params actuales para que
            // el show/edit puedan volver aquí manteniendo filtro + página +
            // sort. `location.search` es la fuente de verdad — todo el
            // filter state ya vive en la URL. El botón "Ver" se eliminó;
            // el click en la fila (`onRow`) ya navega al detalle.
            <Space>
              <Tooltip
                title={t(
                  "certification_sync.button",
                  "Sincronización con Certification",
                )}
              >
                <Button
                  type="text"
                  size="small"
                  icon={<SyncOutlined />}
                  onClick={(e) => {
                    // `onRow` también está escuchando el click a nivel de
                    // fila → sin stopPropagation acabaríamos navegando
                    // a /show y además disparando esta acción.
                    e.stopPropagation();
                    navigate(
                      `/producers/show/${record.id}?action=sync`,
                      { state: { fromList: location.search } },
                    );
                  }}
                />
              </Tooltip>
              {canWrite && (
                <Tooltip title={t("common.edit", "Editar")}>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/producers/edit/${record.id}`, {
                        state: { fromList: location.search },
                      });
                    }}
                  />
                </Tooltip>
              )}
            </Space>
          )}
        />
      </Table>
    </Card>
  );
};
